import { IOpeningHoursOverride, IOverridePeriod, IRegularPeriod } from '@pepperhq/types-sdk';
import { addDays, format, parseISO } from 'date-fns';
import {
    DAY_NUMBER_TO_DAY,
    DAY_TO_DAY_NUMBER,
    DAYS_OF_THE_WEEK,
    DayHours,
    DaysOfTheWeek,
    LocationDayOpeningHours,
    LocationOverrideOpeningHours,
    RegularOpeningHoursFormValues,
    TimeRangeValue
} from './models/LocationModel';

function timeToInputValue(hour: number, minute: number): string {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function inputValueToHM(value: string): { hour: number; minute: number } | undefined {
    if (!value) {
        return undefined;
    }
    const parts = value.split(':');
    const hour = Number(parts[0]);
    const minute = Number(parts[1]);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return undefined;
    }
    return { hour, minute };
}

// Groups a flat slot array by day, preserving midnight-crossing ranges.
// A slot where closeTime.day > openTime.day is stored under openTime.day with
// `to` set to the closing time as-is (e.g. "03:00"). The form validator treats
// to < from as a valid next-day close.
export function apiRegularToFormValues(regular: IRegularPeriod[]): RegularOpeningHoursFormValues {
    const byDay: Record<DaysOfTheWeek, TimeRangeValue[]> = {} as Record<DaysOfTheWeek, TimeRangeValue[]>;
    DAYS_OF_THE_WEEK.forEach(d => {
        byDay[d] = [];
    });

    for (const slot of regular) {
        const day = DAY_NUMBER_TO_DAY[slot.openTime.day];
        if (day === undefined) {
            continue;
        }
        byDay[day].push({
            from: timeToInputValue(slot.openTime.hour, slot.openTime.minute),
            to: timeToInputValue(slot.closeTime.hour, slot.closeTime.minute)
        });
    }

    return DAYS_OF_THE_WEEK.reduce((acc, day) => {
        const ranges = byDay[day];
        const dayHours: DayHours =
            ranges.length === 0
                ? { closed: true, ranges: [{ from: '', to: '' }] }
                : { closed: false, ranges };
        acc[day] = dayHours;
        return acc;
    }, {} as RegularOpeningHoursFormValues);
}

// Converts form values back to the flat slot array the API expects.
// When to < from (midnight-crossing), closeTime.day = (openTime.day + 1) % 7.
export function formValuesToApiRegular(values: RegularOpeningHoursFormValues): IRegularPeriod[] {
    const slots: IRegularPeriod[] = [];

    for (const day of DAYS_OF_THE_WEEK) {
        const { closed, ranges } = values[day];
        if (closed) {
            continue;
        }
        const dayNumber = DAY_TO_DAY_NUMBER[day];

        for (const range of ranges) {
            const open = inputValueToHM(range.from);
            const close = inputValueToHM(range.to);
            if (!open || !close) {
                continue;
            }
            // "00:00" is midnight at the end of the day, i.e. 12am the following day
            const crossesMidnight = range.to === '00:00' || range.to < range.from;
            slots.push({
                openTime: { day: dayNumber, ...open },
                closeTime: { day: crossesMidnight ? (dayNumber + 1) % 7 : dayNumber, ...close }
            });
        }
    }

    return slots;
}

// Converts a single API override to the LocationOverrideOpeningHours
// shape the date override modal uses. closedDates become empty-array entries
// (the existing convention the modal already handles).
export function apiOverrideToFormModel(
    apiOverride: IOpeningHoursOverride<string>
): LocationOverrideOpeningHours {
    const datesMap: Record<string, LocationDayOpeningHours[]> = {};

    for (const slot of apiOverride.dates) {
        const dateKey = slot.openTime.date;
        if (!datesMap[dateKey]) {
            datesMap[dateKey] = [];
        }
        datesMap[dateKey].push({
            openTime: { hour: slot.openTime.hour, minute: slot.openTime.minute },
            closeTime: { hour: slot.closeTime.hour, minute: slot.closeTime.minute }
        });
    }

    for (const closedDate of apiOverride.closedDates) {
        datesMap[closedDate] = [];
    }

    return {
        _id: apiOverride._id ?? '',
        name: apiOverride.name,
        dates: datesMap
    };
}

// Converts the LocationOverrideOpeningHours form model back to the new API
// shape. Empty-array entries become closedDates. Midnight-crossing slots get
// closeTime.date set to the next calendar day.
export function formModelToApiOverride(
    formOverride: LocationOverrideOpeningHours
): IOpeningHoursOverride<string> {
    const dates: IOverridePeriod[] = [];
    const closedDates: string[] = [];

    for (const [dateKey, ranges] of Object.entries(formOverride.dates)) {
        if (ranges.length === 0) {
            closedDates.push(dateKey);
            continue;
        }
        for (const range of ranges) {
            if (!range.openTime || !range.closeTime) {
                continue;
            }
            // {hour: 0, minute: 0} is midnight at the end of the day, i.e. 12am the following day
            const isMidnight = range.closeTime.hour === 0 && range.closeTime.minute === 0;
            const crossesMidnight =
                isMidnight ||
                range.closeTime.hour < range.openTime.hour ||
                (range.closeTime.hour === range.openTime.hour &&
                    range.closeTime.minute < range.openTime.minute);

            const closeDate = crossesMidnight ? format(addDays(parseISO(dateKey), 1), 'yyyy-MM-dd') : dateKey;

            dates.push({
                openTime: { date: dateKey, hour: range.openTime.hour, minute: range.openTime.minute },
                closeTime: { date: closeDate, hour: range.closeTime.hour, minute: range.closeTime.minute }
            });
        }
    }

    return {
        _id: formOverride._id || undefined,
        name: formOverride.name,
        dates,
        closedDates
    };
}
